import { Button } from '@chakra-ui/react';
import { useTranslations } from 'use-intl';

import {
  SubscriptionInfosFragment,
  UserRole,
  useGetSubscriptionQuery,
  useValidateSubscriptionMifidMutation,
} from '@blockpulse3/graphql/hooks';
import { useErrorToast, useSuccessToast } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

type Props = {
  subscriptionId: SubscriptionInfosFragment['id'];
};

export function ValidateMifid({ subscriptionId }: Props): JSX.Element | null {
  const i18nSubscriberDetailsSidePanel = useTranslations('SubscriberDetailsSidePanel');

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const { user } = useUser();

  const [validateMifid, { loading: loadingMifid }] = useValidateSubscriptionMifidMutation();

  const { refetch, loading } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });

  const isUserAuthorized = user?.roles.some((role) =>
    [UserRole.COMPLIANCE_OFFICER, UserRole.SUPERADMIN].includes(role),
  );
  if (!isUserAuthorized) return null;

  const handleMifidValidation = (): void => {
    validateMifid({
      variables: {
        validateSubscriptionMifidInput: {
          subscriptionId,
        },
      },
      onCompleted: () => {
        successToast({
          title: i18nSubscriberDetailsSidePanel('ValidationSteps.Mifid.Toast.Success'),
        });
        refetch();
      },
      onError: (error) => {
        errorToast({
          title: i18nSubscriberDetailsSidePanel('ValidationSteps.Mifid.Toast.Error'),
          description: error.message,
        });
      },
    });
  };

  return (
    <Button
      isDisabled={loadingMifid || loading}
      isLoading={loadingMifid || loading}
      size="sm"
      variant="secondary"
      onClick={handleMifidValidation}
    >
      {i18nSubscriberDetailsSidePanel('ValidationSteps.Mifid.Validate')}
    </Button>
  );
}
